import React, {
	memo,
	useCallback,
	useState,
	useEffect,
	type ComponentType,
	type ReactElement,
} from 'react';
import { styled } from '@compiled/react';
import type { TriggerProps } from '@atlaskit/popup';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { popupWidth } from '../constants.tsx';
import Trigger from './trigger/index.tsx';

type Props = {
	minWidth?: number;
	renderContent: (closeMenu: () => void) => ReactElement;
	forceVisibility?: boolean;
};

const SettingMenuCore: ComponentType<Props> = memo(
	({ renderContent, minWidth = popupWidth, forceVisibility = false }: Props) => {
		const [isOpen, setIsOpen] = useState(forceVisibility);
		useEffect(() => {
			setIsOpen(forceVisibility);
		}, [forceVisibility]);

		const closeMenu = useCallback(() => setIsOpen(false), []);
		const toggleMenu = useCallback(() => setIsOpen((oldIsOpen) => !oldIsOpen), []);

		const content = useCallback(
			() => <DropdownWrapper width={minWidth}>{renderContent(closeMenu)}</DropdownWrapper>,
			[closeMenu, renderContent, minWidth],
		);

		const trigger = useCallback(
			(triggerProps: TriggerProps) => (
				<Trigger {...triggerProps} isSelected={isOpen} onClick={toggleMenu} />
			),
			[isOpen, toggleMenu],
		);

		return (
			<UFOSegment name="business-settings-menu">
				<Popup
					isOpen={isOpen}
					onClose={closeMenu}
					placement="bottom-end"
					messageId="business-settings-menu.ui.settings-menu.settings-menu-core.popup"
					messageType="transactional"
					content={content}
					trigger={trigger}
				/>
			</UFOSegment>
		);
	},
);

export default SettingMenuCore;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const DropdownWrapper = styled.div<{ width: string | number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ width }) => `${width}px`,
});
